import {
  RtFraction,
  RtJsonDate,
  RtRichTextData,
  RtYearMonth,
} from "@aprao/common/src/runtypes/common.js";
import { z } from "zod";

import { salesGroupPredicate } from "../shapes/ShapeRevenueGroup.js";
import { RtBuildPhase } from "./RtBuildPhase.js";
import { RtBuildPreliminary } from "./RtBuildPreliminary.js";
import { RtEscalatedAmount } from "./RtEscalatedAmount.js";
import { RtInterestLender, RtProfitShareLender } from "./RtLender.js";
import { RtOtherCostsGroup } from "./RtOtherCostsGroup.js";
import {
  RtOperatedAssetGroup,
  RtOtherIncomeGroup,
  RtResidentialSalesGroup,
} from "./RtRevenueGroup.js";
import { RtSitePurchase } from "./RtSitePurchase.js";
import { TimelineStageSchema } from "./TimelineStageSchema.js";

/**
 * A type for denormalized metrics data.
 *
 * It is sent by the client along the document so we can surface this data
 * in the lists without having to do calculation at render time. This is an
 * optimisation measure.
 */
export const RtMetrics = z
  .object({
    gdv: RtFraction.nullable(),
    totalCost: RtFraction.nullable(),
    profitOnCost: RtFraction.nullable(),
  })
  .strict();

export const RtReturnTargetDenominator = z.enum(["costs", "gdv"]);

export const RtCashflowOptimizationStrategy = z.enum([
  "minimizeEquity",
  "minimizeFinance",
]);
export const RtCashflowPeriodGranularity = z.enum(["annual", "monthly"]);

export const ExperimentalFlowKindSchema = z.enum(["cashflow", "timeline"]);

export const RtRevenueStage = TimelineStageSchema.extend({
  revenueGroups: z.array(
    z.union([
      RtResidentialSalesGroup,
      RtOperatedAssetGroup,
      RtOtherIncomeGroup,
    ]),
  ),
});

export const RtPurchaseStage = TimelineStageSchema.extend({
  sitePurchases: z.array(RtSitePurchase),
});

export const RtBuildStage = TimelineStageSchema.extend({
  preliminary: RtBuildPreliminary,
  buildPhases: z.array(RtBuildPhase),
});

export const RtOtherCostsStage = TimelineStageSchema.extend({
  otherCostsGroups: z.array(RtOtherCostsGroup),
});

export const RtLendingStage = TimelineStageSchema.extend({
  lenders: z.array(
    z.discriminatedUnion("loanType", [RtInterestLender, RtProfitShareLender]),
  ),
});

export const RtStages = z.object({
  revenue: RtRevenueStage,
  purchase: RtPurchaseStage,
  build: RtBuildStage,
  otherCosts: RtOtherCostsStage,
  lending: RtLendingStage,
});

export const RtAppraisalDocument = z
  .object({
    title: z.string(),
    summary: RtRichTextData,
    returnTarget: RtFraction.nullable(),
    returnTargetDenominator: RtReturnTargetDenominator,
    yearMonthCashflowStart: RtYearMonth.nullable(),
    cashflowOptimizationStrategy: RtCashflowOptimizationStrategy,
    cashflowPeriodGranularity: RtCashflowPeriodGranularity,
    stages: RtStages,
    escalatedCosts: z.array(RtEscalatedAmount),
    escalatedIncomes: z.array(RtEscalatedAmount),
    experimental_flowKind: ExperimentalFlowKindSchema,
  })
  .strict()
  .superRefine(
    // Enforce the relationship constraints around the build phases linking.
    // Since we need the whole context it has to be done at the document level.
    (document, ctx) => {
      const discoveredRefIds = new Set<string>();
      for (const { refId } of document.stages.build.buildPhases) {
        if (discoveredRefIds.has(refId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `No duplicated build phases refId allowed for found ${refId}.`,
          });
        }

        discoveredRefIds.add(refId);
      }

      for (const {
        linkedBuildPhaseRefId,
      } of document.stages.revenue.revenueGroups.filter(salesGroupPredicate)) {
        if (linkedBuildPhaseRefId != null) {
          if (!discoveredRefIds.has(linkedBuildPhaseRefId)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `A revenue groups is linking to an non existing build phase (${linkedBuildPhaseRefId})`,
            });
          }
        }
      }
    },
  )
  .superRefine((document, ctx) => {
    const purchaseWithResidualCalculation =
      document.stages.purchase.sitePurchases.filter(
        ({ pricePurchase }) => pricePurchase.calculationType === "residual",
      );

    if (1 < purchaseWithResidualCalculation.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The appraisal has more than one site purchase with calculation type set to "residual"`,
      });
    }
  });

// fields generated managed by the database (cannot be updated by the user)
const fieldsSynthetic = {
  id: z.number(),
  createdDate: RtJsonDate,
  updatedDate: RtJsonDate,
  version: z.number(),
};

// a record combo of document, and the metrics associated with it
export const RtAppraisalDocumentRelated = z
  .object({
    document: RtAppraisalDocument,
    _extra: z.strictObject({ metrics: RtMetrics }),
  })
  .strict();

export const RtAppraisalCreate = RtAppraisalDocumentRelated.extend({
  projectId: z.number(),
  linkSharingEnabled: z.boolean().optional(),
});

export const RtAppraisal = RtAppraisalDocumentRelated.extend({
  ...fieldsSynthetic,
  project: z.object({ id: z.number() }),
  linkSharingEnabled: z.boolean(),
});

/**
 * A denormalized form of the appraisal used when we only need some top level
 * data from the related appraisals.
 */
export const RtAppraisalSnippet = z
  .object({
    ...fieldsSynthetic,
    title: z.string(),
    metrics: RtMetrics,
  })
  .strict();
